import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { PropostaPendente } from '@remoto/shared';
import { usarConfirmacao } from '../componentes/Modal';
import { IconeAviso } from '../componentes/icones';
import { TextoMarkdown } from '../componentes/chat/TextoMarkdown';
import { mensagemDoErro } from '../erros';
import { tempoRelativo } from '../formato';
import { GESTOS, usarGesto } from './consultas';
import {
  AvisoDeErro,
  BlocoDeDiff,
  BotaoPrimario,
  BotaoSecundario,
  DialogoDeMotivo,
  usarPodeComandar,
} from './pecas';
import { diferencasDeArquivos, hashCurto, manifestoLegivel, mudancasDoManifesto } from './regras';

/**
 * Uma proposta esperando decisão — versão nova, extensão a alvo ou verbete —
 * com Aprovar e Recusar num RODAPÉ FIXO (`web.md`: cartão com ação tem rodapé
 * fixo). O que rola é o miolo, com o diff; o botão que decide nunca depende de
 * rolar até o fim de vinte arquivos para aparecer.
 *
 * A mestre PROPÕE e um humano decide (D19, D20): é aqui que a decisão
 * acontece. Recusar pede motivo; aprovar pergunta antes, porque aprovar uma
 * versão nova a torna a vigente e devolve todo alvo a "só ensaio" até o
 * primeiro caso real dela (sem herança entre versões, F3-desenho §2).
 */

/** De onde a proposta veio: a conversa da mestre (com link) ou uma pessoa pela tela. */
export function OrigemDaProposta({ propostaPor }: { propostaPor: { usuarioId: string | null; sessaoId: string | null } }): React.JSX.Element {
  if (propostaPor.sessaoId) {
    return (
      <>
        proposta na{' '}
        <Link
          to="/sessoes/$id"
          params={{ id: propostaPor.sessaoId }}
          className="text-sky-300 underline-offset-2 hover:underline"
          data-testid="conversa-de-origem"
        >
          conversa de origem
        </Link>
      </>
    );
  }
  return <>proposta pela tela</>;
}

function TituloDaProposta({ proposta }: { proposta: PropostaPendente }): React.JSX.Element {
  switch (proposta.especie) {
    case 'versao':
      return (
        <>
          Versão nova: <span className="font-mono">v{proposta.proposta.numero}</span> de{' '}
          <span className="font-mono">{proposta.rotina.nome}</span>
        </>
      );
    case 'extensao':
      return (
        <>
          Estender <span className="font-mono">{proposta.rotina.nome}</span> ao alvo{' '}
          <span className="font-mono">{proposta.alvo}</span>
        </>
      );
    case 'verbete':
      return (
        <>
          Verbete nº{proposta.verbete.numero}: {proposta.verbete.termo}
        </>
      );
    default: {
      const nenhuma: never = proposta;
      return nenhuma;
    }
  }
}

/** O que a aprovação faz, dito antes de a pessoa confirmar. */
function consequenciaDeAprovar(proposta: PropostaPendente): string {
  switch (proposta.especie) {
    case 'versao':
      return proposta.vigente
        ? `A v${proposta.proposta.numero} passa a ser a vigente no lugar da v${proposta.vigente.numero}, que fica no histórico como superada. Todo alvo volta a "só ensaio" até um caso real conferido da versão nova.`
        : `A v${proposta.proposta.numero} passa a ser a primeira versão vigente da rotina.`;
    case 'extensao':
      return `A rotina passa a valer no alvo ${proposta.alvo}, só em ensaio (fases de leitura) até um caso real conferido ali. Um fluxo validado num alvo não valida outro sem esta decisão (D31).`;
    case 'verbete':
      return 'O texto proposto passa a ser o vigente do verbete; o anterior fica no histórico.';
    default: {
      const nenhuma: never = proposta;
      return nenhuma;
    }
  }
}

function MioloDaVersao({ proposta }: { proposta: Extract<PropostaPendente, { especie: 'versao' }> }): React.JSX.Element {
  const { proposta: nova, vigente } = proposta;
  const mudancas = mudancasDoManifesto(vigente?.manifesto ?? null, nova.manifesto);
  const arquivos = diferencasDeArquivos(vigente?.arquivosComConteudo ?? null, nova.arquivosComConteudo);
  const antesDoManifesto = manifestoLegivel(vigente?.manifesto ?? null);
  const depoisDoManifesto = manifestoLegivel(nova.manifesto);
  const manifestoMudou = antesDoManifesto !== depoisDoManifesto;
  const descricaoMudou = (vigente?.descricao ?? '') !== nova.descricao;
  // Abrir de início só o que mudou, e no máximo três arquivos: cada bloco
  // aberto é um editor do Monaco, e vinte de uma vez travam o telefone.
  const abertos = new Set(arquivos.filter((a) => a.situacao !== 'igual').slice(0, 3).map((a) => a.caminho));
  return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-500">
        {vigente ? (
          <>
            Comparada com a vigente, <span className="font-mono">v{vigente.numero}</span> (
            <span className="font-mono" title={vigente.sha256}>
              {hashCurto(vigente.sha256)}
            </span>
            ). A proposta é <span className="font-mono" title={nova.sha256}>{hashCurto(nova.sha256)}</span>.
          </>
        ) : (
          'Primeira versão da rotina: não há vigente para comparar.'
        )}
      </p>
      {vigente ? (
        mudancas.length > 0 ? (
          <ul className="space-y-1" data-testid="mudancas-do-manifesto">
            {mudancas.map((m) => (
              <li
                key={m.texto}
                className={
                  'flex items-start gap-1.5 rounded px-2 py-1 text-xs ' +
                  (m.grave ? 'bg-red-950/60 font-medium text-red-200 ring-1 ring-red-800' : 'bg-zinc-900/60 text-zinc-300')
                }
              >
                {m.grave ? <IconeAviso tamanho={13} className="mt-px" /> : null}
                <span className="min-w-0 break-words">{m.texto}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-zinc-500">O manifesto não mudou.</p>
        )
      ) : null}
      <BlocoDeDiff
        chave={proposta.id}
        titulo="manifesto.json"
        situacao={vigente ? (manifestoMudou ? 'alterado' : 'igual') : 'novo'}
        antes={antesDoManifesto}
        depois={depoisDoManifesto}
        linguagem="json"
        abertoDeInicio={manifestoMudou}
      />
      {/* A descrição faz parte do hash da versão (R6): também se compara. */}
      <BlocoDeDiff
        chave={proposta.id}
        titulo="descrição"
        situacao={vigente ? (descricaoMudou ? 'alterado' : 'igual') : 'novo'}
        antes={vigente?.descricao ?? ''}
        depois={nova.descricao}
        linguagem="markdown"
        abertoDeInicio={false}
      />
      {arquivos.map((a) => (
        <BlocoDeDiff
          key={a.caminho}
          chave={proposta.id}
          titulo={a.caminho}
          situacao={a.situacao}
          antes={a.antes}
          depois={a.depois}
          abertoDeInicio={abertos.has(a.caminho)}
        />
      ))}
    </div>
  );
}

function MioloDaExtensao({ proposta }: { proposta: Extract<PropostaPendente, { especie: 'extensao' }> }): React.JSX.Element {
  return (
    <div className="space-y-2">
      <p className="text-xs text-zinc-500">Semelhança declarada (o que se afirma igual: acesso, porta, tela, tipos):</p>
      <blockquote
        data-testid="semelhanca-declarada"
        className="whitespace-pre-wrap break-words rounded-md border-l-2 border-sky-700 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-200"
      >
        {proposta.semelhanca}
      </blockquote>
    </div>
  );
}

function MioloDoVerbete({ proposta }: { proposta: Extract<PropostaPendente, { especie: 'verbete' }> }): React.JSX.Element {
  return (
    <div className="space-y-2">
      {proposta.vigente ? (
        <BlocoDeDiff
          chave={proposta.id}
          titulo="texto do verbete"
          situacao={proposta.vigente.texto === proposta.proposta.texto ? 'igual' : 'alterado'}
          antes={proposta.vigente.texto}
          depois={proposta.proposta.texto}
          linguagem="markdown"
          abertoDeInicio
        />
      ) : (
        <div className="rounded-md bg-zinc-900/60 p-3 text-sm">
          <TextoMarkdown texto={proposta.proposta.texto} />
        </div>
      )}
    </div>
  );
}

export function CartaoDeProposta({
  proposta,
  focada = false,
}: {
  proposta: PropostaPendente;
  /** Chegou por "Revisar": a borda acende para a pessoa achar o cartão. */
  focada?: boolean;
}): React.JSX.Element {
  const podeDecidir = usarPodeComandar();
  const decidir = usarGesto(GESTOS.decidir);
  const [recusando, setRecusando] = useState(false);
  const [pedirConfirmacao, dialogo] = usarConfirmacao();

  const quando = proposta.especie === 'versao' ? proposta.proposta.criadoEm : proposta.especie === 'extensao' ? proposta.criadoEm : proposta.proposta.criadoEm;
  const propostaPor = proposta.especie === 'versao' ? proposta.proposta.propostaPor : proposta.propostaPor;
  const motivo = proposta.especie === 'versao' ? proposta.proposta.motivo : proposta.especie === 'verbete' ? proposta.proposta.motivo : null;

  return (
    <section
      id={'proposta-' + proposta.id}
      data-testid="proposta"
      data-especie={proposta.especie}
      aria-label="Proposta esperando decisão"
      className={
        'flex max-h-[80vh] min-h-0 flex-col rounded-lg border bg-sky-950/20 ' +
        (focada ? 'border-sky-400 ring-1 ring-sky-400/40' : 'border-sky-800')
      }
    >
      <header className="shrink-0 space-y-0.5 border-b border-sky-900/60 px-3 py-2">
        <p className="min-w-0 break-words text-sm font-medium text-sky-100">
          <TituloDaProposta proposta={proposta} />
        </p>
        <p className="text-xs text-zinc-500">
          <OrigemDaProposta propostaPor={propostaPor} /> · {tempoRelativo(quando)}
        </p>
        {motivo ? <p className="break-words text-xs text-zinc-300">Motivo: {motivo}</p> : null}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {proposta.especie === 'versao' ? (
          <MioloDaVersao proposta={proposta} />
        ) : proposta.especie === 'extensao' ? (
          <MioloDaExtensao proposta={proposta} />
        ) : (
          <MioloDoVerbete proposta={proposta} />
        )}
      </div>

      {/* Rodapé fixo, fora da rolagem: o botão que decide nunca some. */}
      <footer className="shrink-0 space-y-2 border-t border-sky-900/60 p-3">
        {decidir.error && !recusando ? (
          <AvisoDeErro>{mensagemDoErro(decidir.error, 'Não foi possível decidir agora.')}</AvisoDeErro>
        ) : null}
        {podeDecidir ? (
          <div className="flex flex-wrap gap-2">
            <BotaoPrimario
              className="flex-1"
              disabled={decidir.isPending}
              onClick={() =>
                pedirConfirmacao({
                  titulo: 'Aprovar a proposta',
                  texto: consequenciaDeAprovar(proposta),
                  rotuloConfirmar: 'Aprovar',
                  aoConfirmar: () => decidir.mutate({ id: proposta.id, corpo: { especie: proposta.especie, decisao: 'APROVADA' } }),
                })
              }
            >
              Aprovar
            </BotaoPrimario>
            <BotaoSecundario className="flex-1" disabled={decidir.isPending} onClick={() => setRecusando(true)}>
              Recusar
            </BotaoSecundario>
          </div>
        ) : (
          <p className="text-xs text-zinc-500">Só OPERADOR ou ADMIN decide propostas.</p>
        )}
      </footer>

      {dialogo}
      <DialogoDeMotivo
        titulo="Recusar a proposta"
        aberto={recusando}
        explicacao="A proposta fica no histórico como recusada, com o motivo. Nada muda na versão vigente."
        rotuloDoBotao="Recusar"
        perigoso
        ocupado={decidir.isPending}
        erro={decidir.error ? mensagemDoErro(decidir.error, 'Não foi possível recusar agora.') : null}
        aoFechar={() => setRecusando(false)}
        aoConfirmar={(m) =>
          decidir.mutate(
            { id: proposta.id, corpo: { especie: proposta.especie, decisao: 'RECUSADA', motivo: m } },
            { onSuccess: () => setRecusando(false) },
          )
        }
      />
    </section>
  );
}
